import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, Label, StatusBadge, Empty } from "@/components/common";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const PRESETS = [500, 1000, 2000, 5000];

const TopUpForm = ({ s, onDone }) => {
  const api = useApi();
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const amt = Number(amount) || 0;
  const uri = `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.upi_name)}${amt ? `&am=${amt}` : ""}&cu=INR&tn=${encodeURIComponent("Wallet top-up")}`;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData();
    fd.append("amount", amount); fd.append("utr", utr);
    if (file) fd.append("screenshot", file);
    try { await api("post", "/me/topups", fd); toast.success("Top-up submitted for verification"); setAmount(""); setUtr(""); setFile(null); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Panel className="lg:col-span-2">
      <Label>Add money</Label>
      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(String(p))} data-testid={`topup-preset-${p}`}
                className={`rounded-full border px-4 py-1.5 font-mono text-sm transition-colors ${amt === p ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-[#1F293D] text-slate-300 hover:border-slate-500"}`}>{inr(p)}</button>
            ))}
          </div>
          <Input data-testid="topup-amount-input" type="number" min={100} placeholder="Amount (₹, min 100)" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white p-3" data-testid="topup-qr"><QRCodeSVG value={uri} size={120} /></div>
            <div className="space-y-2 text-sm">
              <p className="text-slate-400">1. Pay {amt ? <span className="text-white">{inr(amt)}</span> : "the amount"} to</p>
              <button type="button" onClick={() => { navigator.clipboard.writeText(s.upi_id); toast.success("UPI ID copied"); }} data-testid="topup-copy-upi-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-1.5 font-mono text-emerald-400">{s.upi_id}<Copy className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-slate-400">2. Enter the 12-digit UTR from your UPI app</p>
          <Input data-testid="topup-utr-input" inputMode="numeric" maxLength={12} value={utr} onChange={(e) => setUtr(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 412345678901" className="h-12 font-mono tracking-widest" />
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#1F293D] bg-[#0B0F17] px-4 py-3 text-sm text-slate-400 hover:border-emerald-500/50" data-testid="topup-screenshot-dropzone">
            <ImagePlus className="h-5 w-5 text-emerald-400" />
            <span className="min-w-0 flex-1 truncate">{file ? file.name : "Attach screenshot (optional)"}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="topup-screenshot-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 5242880) return toast.error("Screenshot must be under 5 MB"); setFile(f || null); }} />
          </label>
          <Button data-testid="topup-submit-btn" type="submit" disabled={busy || amt < 100 || utr.length !== 12} className="h-11 w-full rounded-full font-semibold">{busy ? "Submitting…" : "Submit top-up"}</Button>
          <p className="text-xs text-slate-500">Money appears in your wallet once the admin verifies the payment.</p>
        </form>
      </div>
    </Panel>
  );
};

export default function AddMoney() {
  const api = useApi();
  const { me, refresh } = useOutletContext();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api("get", "/me/topups").then(setRows), [api]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="add-money-page">
      <PageHead label="Wallet" title="Add money">
        <p className="font-mono text-sm text-slate-400">Balance <span className="text-white" data-testid="add-money-balance">{inr(me.user.wallet)}</span></p>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-3">
        <TopUpForm s={me.settings} onDone={() => { load(); refresh(); }} />
        <Panel>
          <Label>Top-up history</Label>
          <div className="mt-4">
            {!rows ? <p className="text-sm text-slate-500">Loading…</p> : rows.length === 0 ? <Empty testId="topup-empty" text="No top-ups yet." /> : (
              <Table data-testid="topup-history-table">
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t.id} data-testid={`topup-row-${t.utr}`}>
                      <TableCell className="text-xs">{fmtDate(t.created_at)}<p className="font-mono text-[10px] text-slate-500">{t.utr}</p></TableCell>
                      <TableCell className="font-mono text-white">{inr(t.amount)}</TableCell>
                      <TableCell><StatusBadge status={t.status} />{t.reason && <p className="mt-1 text-xs text-red-400">{t.reason}</p>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
